import { useMemo, useState, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { useOrg } from "@/hooks/useOrg";
import { FEED_POSTS_QUERY } from "@/api/queries/feedPosts";
import { USERS_WITH_FEED_POSTS_QUERY } from "@/api/queries/usersWithFeedPosts";
import {
  FeedPostStatusEnum,
  FeedPost,
  User,
  FeedPostOrderingInput,
} from "@/__generated__/types";

const PAGE_SIZE = 10;

export const useFeedPostTab = (
  statuses: FeedPostStatusEnum[],
  ordering: FeedPostOrderingInput[],
) => {
  const { org } = useOrg();
  const [pageIndex, setPageIndex] = useState(0);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    postedBy?: string[];
    ctaEnabled?: string[];
  }>({});

  const { data: usersWithPosts } = useQuery(USERS_WITH_FEED_POSTS_QUERY, {
    variables: { orgId: org?.id || "", imageSize: 24 },
    skip: !org?.id,
  });

  const queryVariables = useMemo(() => {
    const vars: {
      orgId: string;
      first?: number;
      last?: number;
      status: FeedPostStatusEnum[];
      postedBy?: string[];
      ctaEnabled?: boolean;
      after?: string;
      before?: string;
      ordering: FeedPostOrderingInput[];
    } = {
      orgId: org?.id || "",
      status: statuses,
      ordering: ordering,
    };

    if (before) {
      vars.last = PAGE_SIZE;
      vars.before = before;
    } else {
      vars.first = PAGE_SIZE;
      if (after) vars.after = after;
    }

    if (selectedFilters.postedBy?.length) {
      vars.postedBy = selectedFilters.postedBy;
    }

    const cta = selectedFilters.ctaEnabled?.[0];
    if (cta === "yes") vars.ctaEnabled = true;
    if (cta === "no") vars.ctaEnabled = false;

    return vars;
  }, [
    org?.id,
    statuses,
    ordering,
    before,
    selectedFilters.postedBy,
    selectedFilters.ctaEnabled,
    after,
  ]);

  const { data: feedPostsData, loading } = useQuery(FEED_POSTS_QUERY, {
    variables: queryVariables,
    skip: !org?.id,
  });

  const feedPosts = useMemo(() => {
    if (feedPostsData?.node?.__typename === "Organization") {
      const posts =
        feedPostsData.node.feedPosts?.edges
          ?.filter(
            (edge): edge is { cursor: string; node: FeedPost } =>
              edge?.node !== null,
          )
          .map((edge) => edge.node) || [];
      return posts;
    }
    return [] as FeedPost[];
  }, [feedPostsData]);

  const pageInfo =
    feedPostsData?.node?.__typename === "Organization"
      ? feedPostsData.node.feedPosts?.pageInfo
      : {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
        };

  const totalCount =
    feedPostsData?.node?.__typename === "Organization"
      ? feedPostsData.node.feedPosts?.totalCount || 0
      : 0;

  const posters = useMemo(() => {
    if (usersWithPosts?.node?.__typename !== "Organization")
      return [] as User[];
    const edges = usersWithPosts.node.users?.edges || [];
    return edges.map((e) => e?.node).filter((n): n is User => !!n);
  }, [usersWithPosts]);

  const handleFilterApply = useCallback(
    (filterId: string, values: string[]) => {
      setSelectedFilters((prev) => ({
        ...prev,
        [filterId]: values,
      }));
      setPageIndex(0);
      setAfter(null);
      setBefore(null);
    },
    [],
  );

  const getFilterDisplayValue = useCallback(() => {
    const activeFilters = Object.values(selectedFilters).filter(
      (filters) => filters && filters.length > 0,
    );
    return activeFilters.length > 0
      ? `${activeFilters.length} active filters`
      : "";
  }, [selectedFilters]);

  const handleNextPage = useCallback(() => {
    if (pageInfo?.hasNextPage) {
      setBefore(null);
      setAfter(pageInfo.endCursor || null);
      setPageIndex(pageIndex + 1);
    }
  }, [pageInfo?.hasNextPage, pageInfo?.endCursor, pageIndex]);

  const handlePreviousPage = useCallback(() => {
    if (pageInfo?.hasPreviousPage) {
      setAfter(null);
      setBefore(pageInfo.startCursor || null);
      setPageIndex(Math.max(0, pageIndex - 1));
    }
  }, [pageInfo?.hasPreviousPage, pageInfo?.startCursor, pageIndex]);

  return {
    feedPostsData: feedPosts,
    loading,
    hasNextPage: pageInfo?.hasNextPage || false,
    hasPreviousPage: pageInfo?.hasPreviousPage || false,
    pageIndex,
    pageCount: Math.ceil(totalCount / PAGE_SIZE),
    feedPosters: posters,
    handleFilterApply,
    getFilterDisplayValue,
    handleNextPage,
    handlePreviousPage,
  };
};
